/**
 * Core Design Tokens
 * Platform-agnostic design system values shareable between Web and React Native
 */

// Light mode colors (Apple-inspired)
export const lightColors = {
  background: "#ffffff",
  backgroundSecondary: "#f5f5f7",
  backgroundElevated: "#ffffff",
  backgroundChrome: "#f8f8fa",
  foreground: "#1d1d1f",
  foregroundSecondary: "#6e6e73",
  foregroundTertiary: "#8e8e93",
  accent: "#0071e3",
  accentHover: "#0077ed",
  accentLight: "#e8f4fd",
  success: "#34c759",
  warning: "#ff9500",
  error: "#ff3b30",
  border: "#d2d2d7",
  borderSubtle: "#e5e5e7",
  separator: "#c6c6c8",
  // Fill colors for backgrounds
  fillPrimary: "#ffffff",
  fillSecondary: "#f5f5f7",
  fillTertiary: "#e8e8ed",
  // Reaction colors
  reactionFire: "#F97316",
  reactionBrain: "#A855F7",
  reactionHeart: "#FF3B6F",
  // Overlay backgrounds
  overlayHeavy: "rgba(0,0,0,0.95)",
  overlayMedium: "rgba(0,0,0,0.7)",
  overlayLight: "rgba(0,0,0,0.5)",
  overlaySubtle: "rgba(0,0,0,0.3)",
  // Liquid glass surfaces (tab bar, etc.)
  glassBackground: "rgba(255,255,255,0.72)",
  glassFill: "rgba(0,0,0,0.10)",
  glassBorder: "rgba(0,0,0,0.06)",
  // Content on accent/overlay surfaces
  onAccent: "#ffffff",
  onOverlay: "#ffffff",
  onOverlaySecondary: "rgba(255,255,255,0.8)",
  onOverlayTertiary: "rgba(255,255,255,0.4)",
  onOverlayButtonBg: "rgba(255,255,255,0.2)",
  // Emotional imprint group tones
  imprintGentleBg: "rgba(255,152,0,0.10)",
  imprintGentleText: "#BF7A00",
  imprintGentleBorder: "rgba(255,152,0,0.18)",
  imprintDeepBg: "rgba(37,99,235,0.10)",
  imprintDeepText: "#3366CC",
  imprintDeepBorder: "rgba(37,99,235,0.18)",
  imprintUneasyBg: "rgba(194,92,48,0.10)",
  imprintUneasyText: "#A85A30",
  imprintUneasyBorder: "rgba(194,92,48,0.18)",
  imprintFlatBg: "rgba(120,120,135,0.10)",
  imprintFlatText: "#70707A",
  imprintFlatBorder: "rgba(120,120,135,0.18)",
} as const;

// Dark mode colors (midnight theme per design system)
export const darkColors = {
  background: "#0B0D14",
  backgroundSecondary: "#151825",
  backgroundElevated: "#1A1D2E",
  backgroundChrome: "#10121A",
  foreground: "#F1F3F9",
  foregroundSecondary: "#9BA1B0",
  foregroundTertiary: "#6B7280",
  accent: "#0a84ff",
  accentHover: "#409cff",
  accentLight: "#1E3A5F",
  success: "#30d158",
  warning: "#ff9f0a",
  error: "#ff453a",
  border: "#2A2E3C",
  borderSubtle: "#1C2235",
  separator: "#252838",
  // Fill colors for backgrounds (elevated surfaces)
  fillPrimary: "#1A1D2E",
  fillSecondary: "#222638",
  fillTertiary: "#2A2E3C",
  // Reaction colors
  reactionFire: "#F97316",
  reactionBrain: "#A855F7",
  reactionHeart: "#FF3B6F",
  // Overlay backgrounds
  overlayHeavy: "rgba(0,0,0,0.95)",
  overlayMedium: "rgba(0,0,0,0.7)",
  overlayLight: "rgba(0,0,0,0.5)",
  overlaySubtle: "rgba(0,0,0,0.3)",
  // Liquid glass surfaces (tab bar, etc.)
  glassBackground: "rgba(11,13,20,0.65)",
  glassFill: "rgba(255,255,255,0.15)",
  glassBorder: "rgba(255,255,255,0.12)",
  // Content on accent/overlay surfaces
  onAccent: "#ffffff",
  onOverlay: "#ffffff",
  onOverlaySecondary: "rgba(255,255,255,0.8)",
  onOverlayTertiary: "rgba(255,255,255,0.4)",
  onOverlayButtonBg: "rgba(255,255,255,0.2)",
  // Emotional imprint group tones
  imprintGentleBg: "rgba(255,183,77,0.15)",
  imprintGentleText: "#FFD599",
  imprintGentleBorder: "rgba(255,183,77,0.25)",
  imprintDeepBg: "rgba(100,160,255,0.15)",
  imprintDeepText: "#93B8FF",
  imprintDeepBorder: "rgba(100,160,255,0.25)",
  imprintUneasyBg: "rgba(235,149,110,0.15)",
  imprintUneasyText: "#E8A882",
  imprintUneasyBorder: "rgba(235,149,110,0.25)",
  imprintFlatBg: "rgba(160,160,175,0.15)",
  imprintFlatText: "#A0A0AF",
  imprintFlatBorder: "rgba(160,160,175,0.22)",
} as const;

// Theme types
export type ThemeName = "light" | "dark";
export type ThemePreference = ThemeName | "system";

// Get colors for a specific theme
export function getColors(theme: ThemeName) {
  return theme === "dark" ? darkColors : lightColors;
}

// Backwards-compatible export (uses light colors by default for existing code)
export const colors = lightColors;

export const spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
} as const;

export const typography = {
  display: {
    fontSize: 56,
    lineHeight: 1.1,
    fontWeight: 600,
    letterSpacing: -0.02,
  },
  headline: {
    fontSize: 40,
    lineHeight: 1.2,
    fontWeight: 600,
    letterSpacing: -0.01,
  },
  title1: {
    fontSize: 32,
    lineHeight: 1.25,
    fontWeight: 600,
    letterSpacing: -0.01,
  },
  title2: {
    fontSize: 28,
    lineHeight: 1.3,
    fontWeight: 600,
    letterSpacing: 0,
  },
  title3: {
    fontSize: 24,
    lineHeight: 1.35,
    fontWeight: 600,
    letterSpacing: 0,
  },
  bodyLarge: {
    fontSize: 21,
    lineHeight: 1.5,
    fontWeight: 400,
    letterSpacing: 0,
  },
  body: {
    fontSize: 17,
    lineHeight: 1.6,
    fontWeight: 400,
    letterSpacing: 0,
  },
  bodySmall: {
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 400,
    letterSpacing: 0,
  },
  caption: {
    fontSize: 12,
    lineHeight: 1.4,
    fontWeight: 400,
    letterSpacing: 0,
  },
  micro: {
    fontSize: 10,
    lineHeight: 1.4,
    fontWeight: 400,
    letterSpacing: 0,
  },
} as const;

export const radii = {
  xs: 2, // Accent bars, progress indicators, subtle rounding
  sm: 4, // Chips, tags, small card accents
  md: 8, // Secondary cards, inputs
  apple: 12, // Standard cards, buttons, inputs
  appleLg: 16, // Large cards, modals
  appleXl: 20, // Hero cards, bottom sheets
  full: 9999, // Pills, avatars, circular elements
} as const;

export const shadows = {
  apple: {
    offsetY: 4,
    blur: 16,
    opacity: 0.08,
  },
  appleHover: {
    offsetY: 6,
    blur: 24,
    opacity: 0.1,
  },
  appleLg: {
    offsetY: 8,
    blur: 32,
    opacity: 0.12,
  },
  appleXl: {
    offsetY: 16,
    blur: 48,
    opacity: 0.16,
  },
} as const;

export const durations = {
  fast: 150,
  normal: 200,
  slow: 300,
  slower: 400,
} as const;

export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

// Type exports for consumers
export interface Colors {
  background: string;
  backgroundSecondary: string;
  backgroundElevated: string;
  backgroundChrome: string;
  foreground: string;
  foregroundSecondary: string;
  foregroundTertiary: string;
  accent: string;
  accentHover: string;
  accentLight: string;
  success: string;
  warning: string;
  error: string;
  border: string;
  borderSubtle: string;
  separator: string;
  fillPrimary: string;
  fillSecondary: string;
  fillTertiary: string;
  reactionFire: string;
  reactionBrain: string;
  reactionHeart: string;
  overlayHeavy: string;
  overlayMedium: string;
  overlayLight: string;
  overlaySubtle: string;
  glassBackground: string;
  glassFill: string;
  glassBorder: string;
  onAccent: string;
  onOverlay: string;
  onOverlaySecondary: string;
  onOverlayTertiary: string;
  onOverlayButtonBg: string;
  imprintGentleBg: string;
  imprintGentleText: string;
  imprintGentleBorder: string;
  imprintDeepBg: string;
  imprintDeepText: string;
  imprintDeepBorder: string;
  imprintUneasyBg: string;
  imprintUneasyText: string;
  imprintUneasyBorder: string;
  imprintFlatBg: string;
  imprintFlatText: string;
  imprintFlatBorder: string;
}
export type Spacing = typeof spacing;
export type Typography = typeof typography;
export type Radii = typeof radii;
export type Shadows = typeof shadows;
export type Durations = typeof durations;
export type Breakpoints = typeof breakpoints;
